import { gqlClient } from '@/api/graphql-client';
import { graphql } from '@/gql';
import {
  GetProjectInvoicesQuery,
  InvoiceFieldsFragment,
  InvoiceStatus,
} from '@/gql/graphql';
import { ReadableInvoiceStatus } from '@payaca/types/invoiceTypes';
import { useQuery } from '@tanstack/react-query';
import projectKeys from './keyFactory';

export const gqlInvoiceStatusToInvoiceStatusMap: Record<
  InvoiceStatus,
  ReadableInvoiceStatus
> = {
  DRAFT: ReadableInvoiceStatus.DRAFT,
  SENT: ReadableInvoiceStatus.SENT,
  PAID: ReadableInvoiceStatus.PAID,
  PARTIALLY_PAID: ReadableInvoiceStatus.PARTIALLY_PAID,
  VOID: ReadableInvoiceStatus.VOID,
};

graphql(`
  fragment InvoiceFields on Invoice {
    id
    publicId
    createdAt
    sentAt
    dueAt
    bouncedAt
    voidedAt
    reference
    customReference
    isDepositInvoice
    status
    totalValue {
      value
      currency {
        id
        code
        exponent
      }
    }
    reconciledPaymentValue {
      value
      currency {
        id
        code
        exponent
      }
    }
    outstandingValue {
      value
      currency {
        id
        code
        exponent
      }
    }
    project {
      id
      publicId
    }
    accountingIntegrationSyncData {
      quickbooks {
        invoiceIdentifier
      }
      xero {
        invoiceIdentifier
      }
    }
    emailTemplate {
      id
      name
    }
  }
`);

const GetProjectInvoices = graphql(`
  query GetProjectInvoices($projectId: ID!) {
    projectById(id: $projectId) {
      id
      invoices {
        ...InvoiceFields
      }
    }
  }
`);

export type Invoice = InvoiceFieldsFragment;

export const useGetProjectInvoices = (projectId?: string) => {
  return useQuery({
    enabled: !!projectId,
    queryKey: projectKeys.invoices(projectId!),
    queryFn: async () => {
      const data = await gqlClient.request<GetProjectInvoicesQuery>(
        GetProjectInvoices,
        {
          projectId: projectId,
        }
      );
      return [...(data.projectById?.invoices || [])].sort((a, b) => {
        return (
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      });
    },
  });
};
